// `@repo/photo-lightbox`: its own package, not `@repo/ui`, because
// `apps/web` cannot depend on `@repo/ui` (the two apps do not share React
// through it, see apps/web/app/components/SupportBubble.tsx). This wrapper
// needs only react + photoswipe, which both apps already have.
import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import 'photoswipe/style.css';
import type PhotoSwipeCore from 'photoswipe';

/**
 * One photo the lightbox can show. `captionScope` is the already-localised
 * "Bài chung" / "Riêng {tên}" fragment — this component never builds
 * Vietnamese itself beyond the two literal chrome labels it is handed.
 */
export interface LightboxPhoto {
  id: string;
  url: string;
  width: number;
  height: number;
  captionScope: string;
}

export interface PhotoLightboxLabels {
  homework: string;
  download: string;
  close: string;
  remove: string;
  counter: (i: number, n: number) => string;
}

export interface PhotoLightboxProps {
  photos: LightboxPhoto[];
  /** `null` closes the lightbox; otherwise the index of the photo to show. */
  index: number | null;
  onIndexChange: (i: number | null) => void;
  subtitle: (photo: LightboxPhoto) => string;
  labels: PhotoLightboxLabels;
  /** Present when opened from the teacher's editing form — swaps the download
   *  button for the ghost-red "Xoá ảnh" button (handoff §4). */
  onDelete?: (photo: LightboxPhoto) => void;
}

// E4 frame 4 ("390 lightbox"), docs/design/design_handoff_note_templates/designs/
// E4 - Anh bai ve nha.dc.html — carried verbatim, not re-described.
const LIGHTBOX_BG = 'oklch(0.14 0.008 158)';
// --color-destructive, packages/ui/src/global.css — the ghost-red "Xoá ảnh" button.
// The frame itself only shows the parent (download) variant; frame 4's own red
// (oklch(0.58 0.2 28)) is the mock's decorative pen-scribble placeholder, not a
// chrome colour, so the teacher-only delete button reuses the app's real
// destructive token instead of inventing a new red.
const DELETE_RED = 'oklch(0.53 0.15 28)';

const ICON_DOWNLOAD =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="20" height="20"><path d="M12 15V3"/><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><path d="m7 10 5 5 5-5"/></svg>';
const ICON_CLOSE =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="22" height="22"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>';
const ICON_TRASH =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="20" height="20"><path d="M10 11v6"/><path d="M14 11v6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/><path d="M3 6h18"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>';

const STYLE_ID = 'lophoc-photo-lightbox-style';
const ROOT_CLASS = 'lophoc-pswp';

/** The mock's 44px-square, radius-10, transparent-on-ink chrome button. */
function chromeButton(): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.style.cssText =
    'width:44px;height:44px;border:none;border-radius:10px;background:transparent;color:#fff;display:inline-flex;align-items:center;justify-content:center;cursor:pointer;padding:0;flex:0 0 auto;';
  return btn;
}

/** Injected once per page: overrides PhotoSwipe's own elements (the backdrop
 *  and the slide `<img>`) that aren't reachable through `ui.registerElement`. */
function ensureGlobalStyle() {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `.${ROOT_CLASS} { --pswp-bg: ${LIGHTBOX_BG}; } .${ROOT_CLASS} .pswp__img { border-radius: 4px; }`;
  document.head.appendChild(style);
}

/**
 * Shared lightbox for homework photos — mock E4 frame 4 ("390 lightbox"),
 * handoff `docs/design/design_handoff_note_templates/21-anh-bai-ve-nha.md` §4.
 * Wraps PhotoSwipe v5: swipe, pinch-zoom, swipe-down-to-close, arrow keys and
 * Esc are its defaults and are configured here, never reimplemented.
 *
 * Theme-agnostic and i18n-free — every visible string arrives through
 * `labels`, `subtitle` and `LightboxPhoto.captionScope`, so `apps/web` (no
 * i18n layer) passes literals and `apps/dashboard` passes `t(...)`.
 *
 * PhotoSwipe is browser-only. Both the CSS (safe at module scope, same as
 * `apps/web/app/routes/huong-dan.tsx`) and the JS module are loaded here —
 * the JS dynamically, after mount — so an SSR consumer never evaluates
 * PhotoSwipe on the server.
 *
 * Controlled component: `index === null` is closed.
 *
 * Mounted as a Radix Dialog layer, not straight into `<body>`: a host that
 * is itself a modal Radix layer (the notes Sheet/Drawer) treats everything
 * outside its layer tree as "outside" — pointer-down there dismisses it,
 * `body` gets `pointer-events: none`, siblings turn `aria-hidden`, focus is
 * trapped. A lightbox appended to `<body>` hit every one of those (closing a
 * photo asked "Bỏ nội dung vừa viết?"; clicks on the photo fell through to
 * the form). As the topmost Radix layer, Escape and clicks are the
 * lightbox's, the host underneath is inert, and focus returns to the
 * thumbnail on close — the same stacking the discard AlertDialog inside the
 * Sheet already relies on. PhotoSwipe's DOM is appended INSIDE the layer via
 * `appendToEl`; the portal is untransformed, so `.pswp`'s `position: fixed`
 * still spans the viewport (it would not inside vaul's translated Drawer).
 */
export function PhotoLightbox({ photos, index, onIndexChange, subtitle, labels, onDelete }: PhotoLightboxProps) {
  const pswpRef = useRef<PhotoSwipeCore | null>(null);
  // Lets the vanilla-DOM handlers PhotoSwipe owns read the latest props
  // without tearing down and recreating the instance on every render.
  const latestRef = useRef({ photos, subtitle, labels, onDelete, onIndexChange });
  latestRef.current = { photos, subtitle, labels, onDelete, onIndexChange };

  const isOpen = index !== null;

  // "A PhotoSwipe instance exists" — true from `init()` until its `destroy`,
  // which lands only after the close fade-out. The Radix layer stays mounted
  // while EITHER the caller wants it open OR an instance is still alive, so
  // React never tears the `.pswp` element out mid-animation. (Unmounting the
  // host early detaches the element, its CSS transition never ends,
  // `destroy` never runs, and the orphaned instance keeps its document
  // listeners: the next open flashed and closed, the one after did nothing.)
  const [alive, setAlive] = useState(false);
  const layerOpen = isOpen || alive;
  // PhotoSwipe appends its DOM here (`appendToEl`). State, not a plain ref,
  // so the open effect re-runs once the layer's content has mounted.
  const [host, setHost] = useState<HTMLDivElement | null>(null);
  const hostRef = useCallback((el: HTMLDivElement | null) => setHost(el), []);

  // Whatever had focus when the caller opened us (the thumbnail button) gets
  // it back on close. Radix's modal content would hand focus to a
  // `Dialog.Trigger`, and there is none, so this does the return explicitly.
  const openerRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (!isOpen) return;
    openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  }, [isOpen]);

  // Open/close lifecycle — deliberately keyed on "should a lightbox exist",
  // not on which index, so navigating between photos never tears the
  // instance down (see the index-sync effect below).
  useEffect(() => {
    if (!isOpen) {
      // Safe on an instance that is already closing; PhotoSwipe ignores it.
      pswpRef.current?.close();
      return;
    }
    // A closing instance still holds the ref: the effect re-runs when its
    // `destroy` flips `alive`, and only then builds the next one.
    if (pswpRef.current || !host) {
      return;
    }

    let cancelled = false;

    (async () => {
      const { default: PhotoSwipe } = await import('photoswipe');
      if (cancelled) return;

      ensureGlobalStyle();

      const openPhotos = latestRef.current.photos;
      const pswp = new PhotoSwipe({
        dataSource: openPhotos.map((photo) => ({
          src: photo.url,
          width: photo.width,
          height: photo.height,
          id: photo.id,
        })),
        index: index ?? 0,
        appendToEl: host,
        // The Radix layer owns focus (trap + return-to-thumbnail) and Escape.
        trapFocus: false,
        returnFocus: false,
        escKey: false,
        mainClass: ROOT_CLASS,
        bgOpacity: 1,
        // Room for the custom top/bottom bars registered below so the image
        // never renders under them: 56+8+44 top, 12+36+~40 bottom (E4 frame 4).
        padding: { top: 110, bottom: 90, left: 12, right: 12 },
        pinchToClose: true,
        closeOnVerticalDrag: true,
        arrowKeys: true,
        // Replaced by the custom top-bar buttons registered in `uiRegister`.
        close: false,
        zoom: false,
        counter: false,
        closeTitle: latestRef.current.labels.close,
      });

      pswp.on('uiRegister', () => {
        pswp.ui?.registerElement({
          name: 'lophocTopBar',
          appendTo: 'bar',
          onInit: (el) => {
            el.style.cssText =
              'width:100%;box-sizing:border-box;padding:56px 12px 8px 16px;display:flex;align-items:center;gap:4px;color:#fff;';

            const counter = document.createElement('span');
            counter.style.cssText =
              'font-size:13.5px;font-weight:500;font-variant-numeric:tabular-nums;color:rgb(255 255 255 / 0.85);';
            el.appendChild(counter);

            const spacer = document.createElement('span');
            spacer.style.cssText = 'flex:1;';
            el.appendChild(spacer);

            let actionEl: HTMLElement;
            if (latestRef.current.onDelete) {
              const removeBtn = chromeButton();
              removeBtn.innerHTML = ICON_TRASH;
              removeBtn.style.color = DELETE_RED;
              removeBtn.setAttribute('aria-label', latestRef.current.labels.remove);
              removeBtn.addEventListener('click', () => {
                const photo = latestRef.current.photos[pswp.currIndex];
                if (photo) latestRef.current.onDelete?.(photo);
              });
              actionEl = removeBtn;
            } else {
              const downloadLink = document.createElement('a');
              downloadLink.style.cssText =
                'width:44px;height:44px;border-radius:10px;background:transparent;color:#fff;display:inline-flex;align-items:center;justify-content:center;text-decoration:none;flex:0 0 auto;';
              downloadLink.innerHTML = ICON_DOWNLOAD;
              downloadLink.setAttribute('aria-label', latestRef.current.labels.download);
              downloadLink.setAttribute('download', '');
              downloadLink.target = '_blank';
              downloadLink.rel = 'noopener';
              actionEl = downloadLink;
            }
            el.appendChild(actionEl);

            const closeBtn = chromeButton();
            closeBtn.innerHTML = ICON_CLOSE;
            closeBtn.setAttribute('aria-label', latestRef.current.labels.close);
            closeBtn.addEventListener('click', () => pswp.close());
            el.appendChild(closeBtn);

            const updateTopBar = () => {
              counter.textContent = latestRef.current.labels.counter(pswp.currIndex + 1, pswp.getNumItems());
              const photo = latestRef.current.photos[pswp.currIndex];
              if (photo && actionEl instanceof HTMLAnchorElement) {
                actionEl.href = photo.url;
              }
            };
            pswp.on('change', updateTopBar);
            updateTopBar();
          },
        });

        pswp.ui?.registerElement({
          name: 'lophocBottomBar',
          appendTo: 'root',
          onInit: (el) => {
            el.style.cssText =
              'position:absolute;left:0;right:0;bottom:0;z-index:10;box-sizing:border-box;padding:12px 16px 36px;display:flex;flex-direction:column;gap:10px;';

            const dotsRow = document.createElement('div');
            dotsRow.style.cssText = 'display:flex;justify-content:center;gap:5px;';
            const dots = latestRef.current.photos.map(() => {
              const dot = document.createElement('span');
              dot.style.cssText = 'width:6px;height:6px;border-radius:999px;flex:0 0 auto;';
              dotsRow.appendChild(dot);
              return dot;
            });
            el.appendChild(dotsRow);

            const captionWrap = document.createElement('div');
            captionWrap.style.cssText = 'text-align:center;';
            const line1 = document.createElement('p');
            line1.style.cssText = 'margin:0;font-size:14px;font-weight:500;color:#fff;';
            const line2 = document.createElement('p');
            line2.style.cssText = 'margin:2px 0 0;font-size:12.5px;color:rgb(255 255 255 / 0.65);';
            captionWrap.appendChild(line1);
            captionWrap.appendChild(line2);
            el.appendChild(captionWrap);

            const updateBottomBar = () => {
              const { photos: currentPhotos, labels: currentLabels, subtitle: currentSubtitle } = latestRef.current;
              dots.forEach((dot, i) => {
                dot.style.background = i === pswp.currIndex ? '#fff' : 'rgb(255 255 255 / 0.35)';
              });
              const photo = currentPhotos[pswp.currIndex];
              if (photo) {
                line1.textContent = photo.captionScope ? `${currentLabels.homework} · ${photo.captionScope}` : currentLabels.homework;
                line2.textContent = currentSubtitle(photo);
              }
            };
            pswp.on('change', updateBottomBar);
            updateBottomBar();
          },
        });
      });

      // Dispatched synchronously at the start of `close()` — the place to
      // sync back to the caller. The ref is NOT dropped here: the instance
      // is still animating and must stay reachable (and mounted) until
      // `destroy`.
      pswp.on('close', () => {
        latestRef.current.onIndexChange(null);
      });
      // Registered up front (a listener added inside `close` would already be
      // gone by the time `destroy` fires). Guarded by identity so a late
      // `destroy` from an earlier instance can never clear a newer one.
      pswp.on('destroy', () => {
        if (pswpRef.current !== pswp) return;
        pswpRef.current = null;
        setAlive(false);
      });

      pswp.init();
      pswpRef.current = pswp;
      setAlive(true);
    })();

    return () => {
      cancelled = true;
    };
    // Re-run only on the closed <-> open transition (and once the host
    // mounts); see the index-sync effect below for navigating between
    // already-open photos.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, host, alive]);

  // Keeps PhotoSwipe's current slide in sync when the caller changes `index`
  // on an already-open lightbox (e.g. opening from a different thumbnail).
  useEffect(() => {
    if (index !== null && pswpRef.current && pswpRef.current.currIndex !== index) {
      pswpRef.current.goTo(index);
    }
  }, [index]);

  // Unmount safety net: if the owning component unmounts while a photo is
  // still open (no prior `close()`), tear PhotoSwipe down immediately.
  useEffect(() => {
    return () => {
      pswpRef.current?.destroy();
      pswpRef.current = null;
    };
  }, []);

  if (!layerOpen) return null;

  return (
    <Dialog.Root
      open
      modal
      onOpenChange={(open) => {
        if (open) return;
        // Escape (Radix owns it — `escKey: false` above). Nothing is ever
        // "outside": `.pswp` is fixed full-viewport inside this content.
        if (pswpRef.current) pswpRef.current.close();
        else latestRef.current.onIndexChange(null);
      }}
    >
      <Dialog.Portal>
        {/* No Overlay: PhotoSwipe paints its own backdrop. The content is an
            empty host; everything visible is PhotoSwipe's DOM inside it. */}
        <Dialog.Content
          ref={hostRef}
          aria-describedby={undefined}
          style={{ outline: 'none' }}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            openerRef.current?.focus();
          }}
        >
          <Dialog.Title style={SR_ONLY}>{labels.homework}</Dialog.Title>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

const SR_ONLY: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};
